import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Divider, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { constants } from '../../constants/constants';
import { WidgetNoData } from './WidgetNoData';
import { CanceledError } from 'axios';
import { IReviewNew } from '../../shared/interfaces/IReviewNew';
import NumberUtils from '../../shared/NumberUtils';
import PlatformImage from '../Logo/PlatformImage';
import CommentingService, { FilterOptions } from '../../services/CommentingService';

/**
 * Widget component that displays rating counts with filtering capabilities.
 * Shows platform logos and the number of ratings in the selected time period.
 */
const WidgetRatingCount: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState<IReviewNew[]>([]);
  const [timeFilter, setTimeFilter] = useState(() => {
    // Try to get the saved filter from localStorage, default to 'week' if not found
    if (typeof window !== 'undefined') {
      const savedFilter = localStorage.getItem('repWidgetRatingCountFilter');
      return savedFilter || 'week';
    }
    return 'week';
  });

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    // Save the filter to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('repWidgetRatingCountFilter', value);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const getReviews = async () => {
      const filter: FilterOptions = {
        pageSize: 1000, // Get a large number to ensure we have enough data for filtering
        offset: 0,
        score: [1, 5],
        withComments: true,
        withoutComments: true,
        type: 'MONITORINGS',
        withGmb: false,
        withMonitoring: true,
      };
      try {
        setLoading(true);
        // Using the same endpoint as in Reviews.tsx
        const data = await CommentingService.getAllReviews(controller, filter);

        if (!data) {
          setLoading(false);
          return;
        } else {
          setLoading(false);
          const reviews = data.reviews ?? [];
          setReviews(reviews);
        }
      } catch (error) {
        if (error instanceof CanceledError) {
        } else {
          setLoading(false);
          console.error(error);
        }
      }
    };

    getReviews().catch((error) => console.log(error));

    return () => {
      controller.abort();
    };
  }, []);

  /**
   * Filters rating data based on selected time period (day, week)
   * and returns filtered data with ratings in the selected period
   */
  const getFilteredData = () => {
    const now = new Date();
    let filterDate = new Date();

    switch (timeFilter) {
      case 'day':
        filterDate.setDate(now.getDate() - 1); // 24 hours
        break;
      case 'month':
        filterDate.setDate(now.getDate() - 30); // 30 days
        break;
      case 'week':
      default:
        filterDate.setDate(now.getDate() - 7); // 7 days
        break;
    }

    // Filter reviews by date
    const filteredReviews = reviews.filter((review) => {
      const reviewDate = new Date(review.timestamp);
      return reviewDate >= filterDate && reviewDate <= now;
    });

    // Group reviews by platform
    const groupedReviews = filteredReviews.reduce((acc, review) => {
      const platform = review.platform;
      if (!acc[platform]) {
        acc[platform] = [];
      }
      acc[platform].push(review);
      return acc;
    }, {} as Record<string, IReviewNew[]>);

    // Convert to array of objects with platform and count
    return Object.entries(groupedReviews).map(([platform, reviews]) => {
      let sum = 0;
      reviews.forEach((r: IReviewNew) => (sum += r.review.rating ?? 0));
      return {
        platform,
        score: sum / reviews.length,
        count: reviews.length,
      };
    });
  };

  const filteredData = getFilteredData();

  return (
    <Box>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                variant={timeFilter === 'day' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('day')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_day')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('week')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_week')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('month')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_month')}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
          <Box sx={{ maxHeight: 230, overflow: 'auto' }}>
            {filteredData.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filteredData.map((item, index) => (
                  <Box
                    key={`${item.platform}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderRadius: 1,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PlatformImage
                        src={constants.platform_logos[item.platform]}
                        alt={item.platform}
                      />
                    </Box>
                    <Tooltip
                      title={t('average_new_ratings')}
                      arrow
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          &#8960;
                        </Typography>
                        <Typography
                          variant="body2"
                          color={theme.palette.primary.main}
                          fontWeight="bolder"
                        >
                          &nbsp;{NumberUtils.formatScore(item.score)}&nbsp;
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          / 5 {t('stars')}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        color={theme.palette.primary.main}
                        fontWeight="bolder"
                      >
                        {item.count}&nbsp;
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {t('widget_ratingcount_ratings')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <WidgetNoData />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WidgetRatingCount;
